// 管理者: セッション履歴(設計書 §3.5.5)
// 状態 / パフォーマー ULID / 期間で絞り込み + ページネーション

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiClientError } from '../../api/client';
import {
  ADMIN_SESSION_STATE_LABELS,
  useAdminSessions,
  type AdminSessionRow,
  type AdminSessionState,
  type AdminSessionsQuery,
} from '../../api/admin/sessions';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

const PER_PAGE = 20;

const STATE_OPTIONS: Array<{ value: AdminSessionState | ''; label: string }> = [
  { value: '', label: 'すべて' },
  { value: 'running', label: '配信中' },
  { value: 'completed', label: '完了' },
  { value: 'failed', label: '失敗' },
  { value: 'starting', label: '開始中' },
  { value: 'stopping', label: '停止処理中' },
];

export function AdminSessionsHistoryPage() {
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState<AdminSessionState | ''>('');
  const [performerUlid, setPerformerUlid] = useState('');
  const [performerDraft, setPerformerDraft] = useState('');

  const params: AdminSessionsQuery = {
    page,
    per_page: PER_PAGE,
    ...(stateFilter ? { state: stateFilter } : {}),
    ...(performerUlid ? { performer_ulid: performerUlid } : {}),
  };
  const sessionsQuery = useAdminSessions(params);

  function applyFilter(setter: () => void) {
    setPage(1);
    setter();
  }

  function applyPerformer(e: React.FormEvent) {
    e.preventDefault();
    applyFilter(() => setPerformerUlid(performerDraft.trim()));
  }

  return (
    <AdminLayout title="セッション履歴">
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select
            label="状態"
            value={stateFilter}
            onChange={(e) =>
              applyFilter(() => setStateFilter(e.target.value as AdminSessionState | ''))
            }
          >
            {STATE_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <form onSubmit={applyPerformer}>
            <Input
              label="パフォーマー ULID(完全一致)"
              value={performerDraft}
              onChange={(e) => setPerformerDraft(e.target.value)}
              placeholder="01HV..."
            />
          </form>
        </div>
      </Card>

      {sessionsQuery.isLoading && !sessionsQuery.data && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {sessionsQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {sessionsQuery.error instanceof ApiClientError
              ? sessionsQuery.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}

      {sessionsQuery.data && sessionsQuery.data.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            該当するセッションはありません。
          </p>
        </Card>
      )}

      {sessionsQuery.data && sessionsQuery.data.data.length > 0 && (
        <Card>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-tertiary)' }}>
                <Th>パフォーマー</Th>
                <Th>状態</Th>
                <Th>サーバー</Th>
                <Th>配信先</Th>
                <Th>開始</Th>
                <Th>配信時間</Th>
                <Th>終了理由</Th>
              </tr>
            </thead>
            <tbody>
              {sessionsQuery.data.data.map((s) => (
                <SessionRow key={s.ulid} session={s} />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {sessionsQuery.data && sessionsQuery.data.pagination.total_pages > 1 && (
        <Card>
          <Pagination
            page={sessionsQuery.data.pagination.page}
            totalPages={sessionsQuery.data.pagination.total_pages}
            total={sessionsQuery.data.pagination.total}
            onChange={setPage}
            disabled={sessionsQuery.isFetching}
          />
        </Card>
      )}
    </AdminLayout>
  );
}

function SessionRow({ session }: { session: AdminSessionRow }) {
  return (
    <tr style={{ borderTop: 'var(--border-default)' }}>
      <Td>
        <Link
          to={`/admin/performers/${session.performer.ulid}`}
          style={{ color: 'var(--color-text-primary)' }}
        >
          {session.performer.display_name}
        </Link>
      </Td>
      <Td>{ADMIN_SESSION_STATE_LABELS[session.state]}</Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {session.server_name} ({session.slot})
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {session.streaming_profile.name}
        <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>
          {session.streaming_profile.platform}
        </div>
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>{formatDateTime(session.started_at)}</Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {formatDuration(session.duration_seconds)}
      </Td>
      <Td style={{ color: 'var(--color-text-tertiary)' }}>
        {session.termination_reason ?? '—'}
      </Td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        fontWeight: 500,
        padding: '8px',
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <td style={{ padding: '10px 8px', verticalAlign: 'top', ...style }}>{children}</td>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (next: number) => void;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
        {page} / {totalPages} ページ(全 {total} 件)
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page <= 1}
            onClick={() => onChange(page - 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            前へ
          </Button>
        </div>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page >= totalPages}
            onClick={() => onChange(page + 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            次へ
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' });
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h} 時間 ${m} 分`;
  return `${m} 分`;
}
